// SCR-FND-03: the app shell per role (frozen P0 mockup). Side menu on wide screens; top bar, content and the
// floating bottom bar on phones, whose "more" opens the full menu as a drawer. Only navigates (GEN-17).
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNav, PullIndicator, SideNav, TopBar, type NavGroup } from '../design/kit';
import { OfflineBar } from '../features/offline/OfflineBar';
import { UpdateRequired, useRequiredUpdate } from '../features/offline/UpdateRequired';
import { keepSellerCache } from '../lib/offline/cache';
import { flushOutbox, setOutboxUser } from '../data/offline';
import { pageEnter } from '../design/motion';
import { PreferencesSheet } from './PreferencesSheet';
import { BOTTOM_PAGES, GROUPS, PAGES, ROLE_PAGES, pageById, pageByPath } from './pages';
import { useSession } from './session';

/** A card page (product, supplier, customer) shows its record's name and a back button in the top bar. */
interface CardHeader {
  title: ReactNode;
  onBack: () => void;
}
const CardHeaderContext = createContext<(header: CardHeader | null) => void>(() => undefined);

export function useCardHeader(title: ReactNode, onBack: () => void) {
  const set = useContext(CardHeaderContext);
  useEffect(() => {
    set({ title, onBack });
    return () => set(null);
  }, [set, title, onBack]);
}

/** The path shown before this one: decides the direction a page enters from (ADR-109). */
let lastPath: string | null = null;
const depth = (path: string) => path.split('/').filter(Boolean).length;

/** Pulling the page down at its top refreshes every query (phones, ADR-109). */
const PULL_PX = 72;
function usePullToRefresh() {
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);
  const start = useRef<number | null>(null);
  useEffect(() => {
    const sheetOpen = () => !!document.querySelector('[role="dialog"]');
    const down = (e: TouchEvent) => {
      start.current = window.scrollY <= 0 && !sheetOpen() ? (e.touches[0]?.clientY ?? null) : null;
    };
    const move = (e: TouchEvent) => {
      if (start.current === null) return;
      const dy = (e.touches[0]?.clientY ?? 0) - start.current;
      setPull(dy > 8 && window.scrollY <= 0 ? Math.min(dy, PULL_PX * 1.5) : 0);
    };
    const up = () => {
      start.current = null;
      setPull((p) => {
        if (p >= PULL_PX) void qc.invalidateQueries();
        return 0;
      });
    };
    window.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', up);
    return () => {
      window.removeEventListener('touchstart', down);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', up);
    };
  }, [qc]);
  return pull;
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { role, userId } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // OFF-C-01: the Seller's phone keeps what it read so it opens without signal, and sends what waited as soon as it can.
  useEffect(() => {
    if (role !== 'seller') return;
    void setOutboxUser(userId).then(flushOutbox);
    return keepSellerCache(qc, userId);
  }, [role, userId, qc]);
  const [drawer, setDrawer] = useState(false);
  const [prefs, setPrefs] = useState(false);
  const [card, setCard] = useState<CardHeader | null>(null);
  // A card route (/products/:id) belongs to its page.
  const current = pageByPath(pathname)?.id ?? pageByPath(`/${pathname.split('/')[1] ?? ''}`)?.id ?? '';
  const allowed = ROLE_PAGES[role];
  const pull = usePullToRefresh();
  const required = useRequiredUpdate();

  // Motion: a card page enters from the inline end, the list it came from from the inline start, a top-level page
  // fades in. Only the path counts: switching tabs (the query) does not move the page.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (lastPath !== null && lastPath !== pathname) {
      const from = depth(pathname) > depth(lastPath) ? 'end' : depth(pathname) < depth(lastPath) ? 'start' : 'fade';
      pageEnter(contentRef.current, from);
    }
    lastPath = pathname;
  }, [pathname]);

  // Keep the context (truck, round, working date) and the dev role in the URL when moving between pages.
  const go = (id: string) => {
    const page = pageById(id);
    if (!page) return;
    setDrawer(false);
    navigate({ pathname: page.path, search });
  };

  const groups: NavGroup[] = GROUPS.map((g) => ({
    caption: t(g),
    items: PAGES.filter((p) => p.group === g && allowed.includes(p.id)).map((p) => ({
      id: p.id,
      label: t(`p.${p.id}`),
      icon: p.icon,
    })),
  }));
  const bottom = BOTTOM_PAGES[role].map((id) => {
    const p = pageById(id);
    if (!p) throw new Error(`Unknown bottom page ${id}`);
    return { id, label: id === 'dashboard' ? t('p.home') : t(`p.${id}`), icon: p.icon };
  });
  const menuLabel = t('more');
  const footer = {
    label: t('pref.open'),
    icon: 'gear' as const,
    onSelect: () => {
      setDrawer(false);
      setPrefs(true);
    },
  };
  const title = current ? t(`p.${current}`) : t('s6.title');

  return (
    <div className="yv-shell">
      <aside className="yv-shell__side">
        <SideNav label={menuLabel} groups={groups} current={current} onNavigate={go} footer={footer} />
      </aside>
      <div className="yv-shell__main">
        <TopBar
          title={card?.title ?? title}
          roleLabel={t(`role.${role}`)}
          menuLabel={menuLabel}
          onMenu={() => setDrawer(true)}
          onBack={card?.onBack}
          backLabel={t('back')}
        />
        <main className="yv-shell__content">
          <PullIndicator pull={pull} ready={pull >= PULL_PX} label={t(pull >= PULL_PX ? 'ptr.release' : 'ptr.pull')} />
          <div className="yv-shell__page" ref={contentRef}>
            <OfflineBar />
            {required ? (
              <UpdateRequired required={required} />
            ) : (
              <CardHeaderContext.Provider value={setCard}>{children}</CardHeaderContext.Provider>
            )}
          </div>
        </main>
        <div className="yv-shell__bottom">
          <BottomNav
            label={menuLabel}
            items={bottom}
            current={current}
            onNavigate={go}
            more={{ label: t('more'), onOpen: () => setDrawer(true) }}
          />
        </div>
      </div>
      {drawer && (
        <div className="yv-shell__drawer" data-testid="drawer">
          <SideNav label={menuLabel} groups={groups} current={current} onNavigate={go} footer={footer} />
          <div className="yv-scrim" onClick={() => setDrawer(false)} data-testid="drawer-scrim" />
        </div>
      )}
      {prefs && <PreferencesSheet onClose={() => setPrefs(false)} />}
    </div>
  );
}
